import { NgModule, provideZoneChangeDetection } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import {
  AutoRefreshTokenService,
  createInterceptorCondition,
  INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
  IncludeBearerTokenCondition,
  includeBearerTokenInterceptor,
  provideKeycloak,
  UserActivityService,
  withAutoRefreshToken,
} from 'keycloak-angular';
import { environment } from '../environments/environment';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeng/themes/aura';
import { ButtonModule } from 'primeng/button';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { appRoutes } from './app.routes';
import { CommonModule } from '@angular/common';
import { FormBuilderComponent } from './components/form-builder/form-builder.component';
import { provideTranslateService, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { MessageService } from 'primeng/api';
import { TranslationLoaderService } from '@/services/translation-loader.service';
import { SpinnerComponent } from './components/shared/spinner/spinner.component';
import { APP_INITIALIZER } from '@angular/core';
import { acceptLanguageInterceptor } from './interceptors/language.interceptor';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './i18n/', '/common.json');
}

const urlCondition = createInterceptorCondition<IncludeBearerTokenCondition>({
  urlPattern: environment.api.urlPattern,
  bearerPrefix: 'Bearer',
});
export function initTranslations(loader: TranslationLoaderService, translate: TranslateService) {
  return () => {
    const appState = JSON.parse(localStorage.getItem('app-state') || '{}');
    const lang = appState.language || translate.getBrowserLang() || translate.defaultLang || 'fr';
    translate.setDefaultLang(lang);
    translate.use(lang);
    return loader.loadInitialNamespaces(lang, [
      'common',
      'milestone',
      'paa',
      'ppm',
      'ao',
      'dac',
      'settings',
      'meeting',
      'transanctional-form',
      'clarification',
      'funding-sources',
      'dac-general-params',
      'clarification',
    ]);
  };
}
@NgModule({
  declarations: [AppComponent],
  imports: [
    // Modules
    AppRoutingModule,
    BrowserModule,
    ButtonModule,
    CommonModule,
    // Components
    FormBuilderComponent,
    SpinnerComponent,
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: initTranslations,
      deps: [TranslationLoaderService, TranslateService],
      multi: true,
    },
    provideKeycloak({
      config: {
        url: environment.keycloak.url,
        realm: environment.keycloak.realm,
        clientId: environment.keycloak.clientId,
      },
      initOptions: {
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
        redirectUri: window.location.origin + '/',
        checkLoginIframe: false,
      },
      features: [
        withAutoRefreshToken({
          onInactivityTimeout: 'logout',
          sessionTimeout: 10800000,
        }),
      ],
      providers: [AutoRefreshTokenService, UserActivityService],
    }),
    {
      provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
      useValue: [urlCondition],
    },
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      appRoutes,
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'enabled',
      })
      // withEnabledBlockingInitialNavigation()
    ),
    provideHttpClient(withInterceptors([includeBearerTokenInterceptor, acceptLanguageInterceptor])),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          darkModeSelector: '.app-dark',
          cssLayer: {
            name: 'primeng',
            order: 'tailwind-base, primeng, tailwind-utilities',
          },
        },
      },
    }),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
      defaultLanguage: environment.defaultLanguage,
    }),
    MessageService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  /*translationNamespaces = ['common', 'milestone', 'paa', 'ppm', 'settings', 'meeting'];
  constructor(private translationLoader: TranslationLoaderService) {
    this.translationLoader.initialize(this.translationNamespaces);
  }*/
}
